import { useEffect, useState } from "react";
import { BRAND } from "@core/brand/brand-config";
import { Search } from "react-feather";
import LoaderImg from "@assets/images/loader.gif";

const TrademoFAQAccordion = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const highlightText = (text, highlight) => {
    if (!highlight) return text;

    const escapedHighlight = escapeRegExp(highlight);
    const parts = text.split(new RegExp(`(${escapedHighlight})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="tw-bg-yellow-200 tw-font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  const faqData = [
    {
      question: `What are the credit pools in ${BRAND.name}?`,
      answer: (
        <div>
          <p className="tw-mb-3 tw-text-sm tw-font-normal">{BRAND.name} uses two types of credit pools to manage feature usage:</p>
          <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-gray-700 tw-font-medium tw-text-sm">Download Credit Pool</strong>: For actions like shipment downloads, company downloads, and analytics dash-let downloads.
            </li>
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-gray-700 tw-font-medium tw-text-sm">Value Add Credit Pool</strong>: For premium features such as accessing contact information (email/phone) and due diligence reports.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "What actions consume Download Credits?",
      answer: (
        <div>
          <p className="tw-mb-3 tw-text-sm tw-font-normal">Download Credits are used for:</p>
          <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Shipment Download</strong> – <span className=" tw-text-primary-purple-600 tw-bg-primary-purple-50 tw-px-2 tw-py-1 tw-rounded-xl tw-font-medium">1 credit</span> per shipment
            </li>
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Company Download</strong> – <span className=" tw-text-primary-purple-600 tw-bg-primary-purple-50 tw-px-2 tw-py-1 tw-rounded-xl tw-font-medium">5 credits</span> per company
            </li>
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Analytics Dash-let Download</strong> – <span className=" tw-text-primary-purple-600 tw-bg-primary-purple-50 tw-px-2 tw-py-1 tw-rounded-xl tw-font-medium">1 credit</span> per report row
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "What actions consume Value Add Credits?",
      answer: (
        <div>
          <p className="tw-mb-3 tw-text-sm tw-font-normal">Value Add Credits are used for:</p>
          <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Due Diligence Report</strong> – <span className=" tw-text-primary-purple-600 tw-bg-primary-purple-50 tw-px-2 tw-py-1 tw-rounded-xl tw-font-medium">500 credits</span> per report
            </li>
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Contact (Phone)</strong> – <span className=" tw-text-primary-purple-600 tw-bg-primary-purple-50 tw-px-2 tw-py-1 tw-rounded-xl tw-font-medium">5 credits</span> per phone number
            </li>
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Contact (Email)</strong> – <span className=" tw-text-primary-purple-600 tw-bg-primary-purple-50 tw-px-2 tw-py-1 tw-rounded-xl tw-font-medium">1 credit</span> per email
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "Will I be notified before credits are deducted?",
      answer: (
        <div>
          <p className="tw-mb-3 tw-text-sm tw-font-normal">Yes. Before any credit-consuming action is performed, the system will display a confirmation message showing:</p>
          <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
            <li className="tw-font-normal tw-text-sm">Number of items selected</li>
            <li className="tw-font-normal tw-text-sm">Number of credits that will be deducted</li>
            <li className="tw-font-normal tw-text-sm">Type of credit pool being used</li>
          </ul>
        </div>
      ),
    },
    {
      question: "What happens if I don't have enough credits?",
      answer: (
        <div>
          <p className="tw-text-sm tw-font-normal">If you don't have enough credits, you will not be able to proceed with the action. You can request additional credits by contacting the Customer Success team from within the product interface.</p>
        </div>
      ),
    },
    {
      question: "What happened to my earlier credits for contacts or due diligence?",
      answer: (
        <div>
          <p className="tw-mb-3 tw-text-sm tw-font-normal">All legacy credits have been migrated into the new credit pools:</p>
          <ul className="tw-list-disc tw-pl-5 tw-space-y-2">
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Phone contact credits</strong> were converted into <span className="tw-text-gray-700 tw-font-medium tw-text-sm">Value Add Credits</span> (1 phone = 5 credits)
            </li>
            <li className="tw-font-normal tw-text-sm">
              <strong className="tw-text-sm tw-font-medium">Email contact credits</strong> were converted into <span className="tw-text-gray-700 tw-font-medium tw-text-sm">Value Add Credits</span> (1 email = 1 credit)
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: "Where can I view my current credit balances?",
      answer: (
        <div>
          <p className="tw-text-sm tw-font-normal">
            Admins can view your current <span className="tw-text-gray-700 tw-font-medium tw-text-sm">Download Credits</span> and <span className="tw-text-gray-700 tw-font-medium tw-text-sm">Value Add Credits</span> under the Credits section of the UMS. For download credits and value add credits, usage can be viewed at <strong className="tw-text-sm tw-font-medium">Profile Icon &gt;&gt; UMS &gt;&gt; Credits &gt;&gt; {BRAND.shortName}</strong>.
          </p>
        </div>
      ),
    },
    {
      question: "Who do I contact for credit-related issues?",
      answer: (
        <div>
          <p className="tw-text-sm tw-font-normal">
            Please contact{" "}
            <strong className="tw-text-sm tw-font-medium">
              <a href="mailto:customersuccess@trademo" className="tw-text-primary-purple-600">
                customersuccess@trademo
              </a>
            </strong>{" "}
            or reach out to your <strong className="tw-text-sm tw-font-medium">Sales Executive</strong> for any questions or issues related to credits.
          </p>
        </div>
      ),
    },
  ];
  const filteredFaqData = faqData.filter((item) => item.question.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="tw-flex tw-flex-col tw-bg-gray-25 tw-gap-6 tw-h-[calc(100vh-48px)] tw-items-center">
      <div className="tw-flex tw-flex-col tw-items-center tw-mt-5 ">
        <div className="tw-text-base tw-font-medium">Frequently Asked Questions</div>
        <div className="tw-flex tw-items-center tw-relative tw-border tw-border-gray-200 tw-rounded-full tw-px-3 tw-py-2 tw-mt-2 tw-min-w-[460px] tw-shadow-button tw-bg-white">
          <Search size={14} color="#101828" className="" />
          <input type="text" className="tw-w-full tw-py-0.5  tw-rounded-lg tw-pl-3" placeholder={`Search FAQs`} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>
      {loading ? (
        <div className="loader-container">
          <div className="loader-block">
            <img src={LoaderImg} alt="description of gif" />
          </div>
        </div>
      ) : (
        <>
          <div className="tw-w-[70%] tw-bg-white tw-p-3 tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden tw-max-h-[70%]">
            <div className="tw-text-base tw-font-semibold tw-text-gray-900 tw-py-2">Credits</div>
            <div className="tw-divide-y tw-divide-gray-200 tw-max-h-[85%] tw-overflow-auto">
              {filteredFaqData.length > 0 ? (
                filteredFaqData.map((item, index) => (
                  <div key={item} className="tw-border-b-0 tw-border-gray-200 last:tw-border-b-0 tw-border-t-0">
                    <button onClick={() => toggleAccordion(index)} className={`tw-w-full tw-px-6 tw-py-5 ${filteredFaqData.length === 1 ? "" : "tw-pb-2"} tw-text-left tw-flex tw-justify-between tw-items-center tw-transition-all tw-duration-200 tw-bg-white ${activeIndex === index ? "" : "tw-bg-white tw-text-gray-900"}`}>
                      <span className="tw-font-medium tw-text-sm tw-pr-4">{highlightText(item.question, searchTerm.trim())}</span>
                      <span className={`material-symbols-outlined mr-1`}>{activeIndex === index ? "do_not_disturb_on" : "add_circle"}</span>
                    </button>

                    <div className={`tw-overflow-hidden tw-transition-all tw-duration-300 ${activeIndex === index ? "tw-max-h-96 tw-opacity-100" : "tw-max-h-0 tw-opacity-0"}`}>
                      <div className="tw-px-6 tw-py-5 tw-bg-white tw-pt-2 tw-text-gray-700 tw-leading-relaxed">{item.answer}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="tw-px-6 tw-py-8 tw-bg-white tw-text-center tw-text-gray-500">
                  <p className="tw-text-sm">No FAQs found matching your search.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TrademoFAQAccordion;
